<template>
    <div>
        <div class="alert alert-danger mb-3 text-center" role="alert" v-if="showError">
            {{ $t('checkout.cart_summary_error') }}
            <button v-if="redirect" class="btn btn-sm btn-outline-light ms-2" @click="redirectToCart">
                {{ $t('checkout.return_to_cart') }}
            </button>
            <button v-else class="btn btn-sm btn-outline-light ms-2" @click="retry">
                {{ $t('checkout.retry') }}
            </button>
        </div>

        <div class="card checkout-card checkout-card-payment-totals" v-if="!showError">
            <div class="card-body">
                <h3 class="mb-3">{{ $t('checkout.payment_total_amount') }}</h3>

                <div class="label-value-row">
                    <span class="label">{{ $t('checkout.payment_total') }}</span>
                    <span v-if="!updatingQuantityItems" class="value fw-bold">&euro;{{ formatNumber(positionPrice) }}</span>
                    <span v-if="updatingQuantityItems" class="spinner-border spinner-border" role="status" aria-hidden="true"></span>
                </div>

                <div class="label-value-row">
                    <span class="label">{{ $t('checkout.payment_delivery_cost') }}</span>
                    <span v-if="!updatingShippingMethod && !hasShippingCosts" class="value fw-bold">
                        -
                    </span>

                    <span v-if="!updatingShippingMethod && hasShippingCosts" class="value fw-bold">
                        &euro;{{ formatNumber(shippingCosts) }}
                    </span>

                    <span v-if="updatingShippingMethod" class="spinner-border spinner-border" role="status" aria-hidden="true"></span>
                </div>

                <div v-if="discounts && discounts.length > 0" class="label-value-row text-success">
                    <span class="label">{{ $t('checkout.payment_discounts') }}</span>
                    <span v-if="!updatingQuantityItems" class="value fw-bold">
                        -&euro;{{ formatNumber(totalDiscounts) }}
                    </span>
                    <span v-if="updatingQuantityItems" class="spinner-border spinner-border" role="status" aria-hidden="true"></span>
                </div>

                <hr />

                <div class="label-value-row">
                    <span class="label">{{ $t('checkout.payment_final_total') }}</span>
                    <span v-if="!updatingQuantityItems && !updatingShippingMethod" class="value fw-bold">
                        &euro;{{ formatNumber(totalPrice) }}
                    </span>
                    <span v-if="updatingQuantityItems || updatingShippingMethod" class="spinner-border spinner-border" role="status" aria-hidden="true"></span>
                </div>

                <div v-if="isTaxIncluded" class="label-value-row small text-muted">
                    <span class="label">{{ $t('checkout.payment_tax_included') }}</span>
                    <span class="value">&euro;{{ formatNumber(taxAmount) }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import shopware from '../../mixins/shopware';
import formatters from '../../mixins/formatters';
import { EventBus } from '../../api/eventBus.ts';

export default {
    name: 'CartSummary',
    mixins: [shopware, formatters],
    props: {
        cart: Object|null,
        redirect: String
    },
    data() {
        return {
            internalCart: null,
            updatingQuantityItems: false,
            updatingShippingMethod: false,
            positionPrice: null,
            shippingCosts: null,
            totalPrice: null,
            taxAmount: null,
            discounts: [],
            totalDiscounts: 0,
            showError: false,
            retryCount: 0,
            maxRetries: 3
        }
    },
    created: function () {
        this.initialise();
        EventBus.$on('updating-quantity-items', this.updateQuantityItems);
        EventBus.$on('updating-shipping-method', this.updateShippingMethod);
        EventBus.$on('cart-updated', this.handleCartUpdate);
    },
    beforeDestroy() {
        EventBus.$off('updating-quantity-items', this.updateQuantityItems);
        EventBus.$off('updating-shipping-method', this.updateShippingMethod);
        EventBus.$off('cart-updated', this.handleCartUpdate);
    },
    computed: {
        cartData() {
            return this.internalCart || this.cart;
        },
        hasShippingCosts() {
            return this.shippingCosts > 0;
        },
        isTaxIncluded() {
            return this.taxAmount > 0;
        }
    },
    methods: {
        initialise() {
            if (!this.cart) {
                this.fetchCart();
            } else {
                this.constructSummary();
            }
        },

        retry() {
            this.showError = false;
            this.initialise();
        },

        redirectToCart() {
            window.location.href = this.redirect;
        },

        handleCartUpdate(cart) {
            if (cart) {
                this.internalCart = cart;
                this.constructSummary();
            }
        },

        async fetchCart() {
            try {
                this.internalCart = await this.api.client.invoke("readCart get /checkout/cart");
                this.constructSummary();
            } catch (error) {
                console.error("Error fetching cart:", error);

                // Implement retry logic
                if (this.retryCount < this.maxRetries) {
                    this.retryCount++;
                    console.log(`Retrying cart fetch (${this.retryCount}/${this.maxRetries})...`);
                    await new Promise(resolve => setTimeout(resolve, 1000)); // Wait before retry
                    await this.fetchCart();
                } else {
                    this.showError = true;
                    this.retryCount = 0;

                    if (this.redirect) {
                        setTimeout(() => {
                            window.location.href = this.redirect;
                        }, 3000);
                    }
                }
            }
        },

        async constructSummary() {
            try {
                if (!this.cartData) {
                    throw new Error("Cart data not available");
                }

                // Extract basic pricing information
                this.positionPrice = this.cartData?.price?.positionPrice || 0;
                this.shippingCosts = this.cartData?.deliveries?.[0]?.shippingCosts?.totalPrice || 0;
                this.totalPrice = this.cartData?.price?.totalPrice || 0;
                this.taxAmount = this.cartData?.price?.calculatedTaxes?.[0]?.tax || 0;

                // Calculate discounts from promotions and vouchers
                this.discounts = [];
                this.totalDiscounts = 0;

                // Check for promotion line items
                if (this.cartData.lineItems) {
                    const promotionItems = this.cartData.lineItems.filter(item =>
                        item.type === 'promotion' || (item.good === false && item.price && item.price.totalPrice < 0)
                    );

                    if (promotionItems.length > 0) {
                        this.discounts = promotionItems.map(item => ({
                            name: item.label,
                            amount: Math.abs(item.price.totalPrice)
                        }));

                        this.totalDiscounts = promotionItems.reduce(
                            (sum, item) => sum + Math.abs(item.price.totalPrice), 0
                        );
                    }
                }
            } catch (error) {
                console.error("Error constructing cart summary:", error);
                this.showError = true;

                if (this.redirect) {
                    setTimeout(() => {
                        window.location.href = this.redirect;
                    }, 3000);
                }
            } finally {
                this.updatingQuantityItems = false;
                this.updatingShippingMethod = false;
            }
        },

        updateQuantityItems(status) {
            // Update cart when updating items ended instead of status change
            if (this.updatingQuantityItems && !status) {
                this.fetchCart();
                return;
            }

            this.updatingQuantityItems = status;
        },

        updateShippingMethod(status) {
            // Update cart when updating shipping method ended instead of status change
            if (this.updatingShippingMethod && !status) {
                this.fetchCart();
                return;
            }

            this.updatingShippingMethod = status;
        },
    }
};
</script>
